import styles from './discussion-comment.module.css'

import { PiThumbsUp } from '@react-icons/all-files/pi/PiThumbsUp'
import { PiThumbsUpFill } from '@react-icons/all-files/pi/PiThumbsUpFill'
import * as React from 'react'

import { DISCUSSION_REACTION_LIKE } from '@disco/constants/ReactionTypes'
import { useToggleDiscussionReactionMutation } from '@disco/state-management/api/discussions'

import type { Discussion } from 'types/discussion'

import { Button, Flex, Text, Tooltip } from 'components/ds'

import { useSelector } from 'hooks/use-redux'

type Props = {
  discussion: Discussion
}

const LikeButton = ({ discussion }: Props): React.ReactElement => {
  const [toggleReaction, { isLoading }] = useToggleDiscussionReactionMutation()
  const currentUserId = useSelector(state => state.user?.id)

  const likes = discussion.reactions.filter(
    r => r.type === DISCUSSION_REACTION_LIKE,
  )
  const hasLikes = likes.length > 0
  const userLiked =
    hasLikes &&
    typeof currentUserId === 'number' &&
    likes.some(l => l.user.id === currentUserId)

  const handleClick = () => {
    if (isLoading) return
    toggleReaction({
      id: discussion.id,
      parent: discussion.parent,
      target_type: discussion.target_type,
      target_object_id: discussion.target_object_id,
      type: DISCUSSION_REACTION_LIKE,
    })
  }

  const tooltipContent =
    likes.length > 0
      ? likes
          .map(l => `@${l.user.first_name} ${l.user.last_name}`.trim())
          .join(', ')
      : null

  const button = (
    <Button
      size="1"
      variant={'soft'}
      color={userLiked ? 'blue' : 'gray'}
      onClick={handleClick}
      disabled={isLoading}
      className={!hasLikes && styles.likeButtonEmpty}
      aria-pressed={userLiked}
      aria-label={userLiked ? 'Remove like' : 'Like'}
      mr={hasLikes ? '1' : '0'}
    >
      {userLiked ? <PiThumbsUpFill size={14} /> : <PiThumbsUp size={14} />}
      {likes.length > 0 && (
        <Text size="1" color="gray">
          {likes.length}
        </Text>
      )}
    </Button>
  )

  return (
    <Flex align="center" gap="1" className={styles.likeButton}>
      {tooltipContent ? (
        <Tooltip
          className={styles.likeButtonToolTip}
          content={tooltipContent}
          align="start"
        >
          {button}
        </Tooltip>
      ) : (
        button
      )}
    </Flex>
  )
}

export default LikeButton
